import type { Target } from './attention';
import type { MortalPresence } from './mortalPresence';
import type { MortalVerb } from './mortalVerbs';

/**
 * Which body the player is in — the ONE source of truth about it
 * (2026-08-19, the mortal inhabitation skeleton).
 *
 *     deity    the orbit eye: free cursor, W A S D carry the attention, the
 *              wheel is the scale of it, 1–4 are the divine verbs
 *     mortal   a pair of feet on the ground: captured mouse, W A S D walk,
 *              Shift runs, Space jumps, 1–4 are a mortal palette that does
 *              nothing yet
 *
 * Nothing infers the mode from the camera's height, from what is chosen, from
 * the HUD or from whether the pointer is locked. `PlayerModes.mode` is read, and
 * `inhabit`/`ascend` are the only two ways it changes. Each transition hands
 * input ownership across — exactly one body is `active` at a time, and the
 * body that is not drops every key and every pixel of mouse it is sent — so
 * W cannot walk the mortal and carry the god in the same frame (brief §26, §27).
 *
 * This module is deliberately DOM-free and three-free and imports nothing from
 * the simulation: it is the whole of mode ownership, and `test/inhabit.ts`
 * runs it against a real `Simulation` and a real `DeityPresence` under tsx.
 * What it does NOT do is everything that needs the page — the pointer lock,
 * the camera's field of view, the HUD's palette swap — which `main.ts` does
 * around these two calls, in one place each.
 */
export type PlayerMode = 'deity' | 'mortal';

/** What the controller needs of the deity's body: whose input it is, and which way it faces. */
export interface DeityBody {
  active: boolean;
  readonly yaw: number;
  clearInput(): void;
}

/** A person's present standing place, through the same truth the eye follows them by. */
export type LocatePerson = (personId: string) => { x: number; y: number; z: number } | null;

export type InhabitResult =
  | { ok: true; id: string; name: string }
  /**
   *  no-person   nothing, or something that is not a person, is chosen
   *  gone        the chosen person is not in the world any more
   *  not-deity   already in a mortal body
   */
  | { ok: false; reason: 'no-person' | 'gone' | 'not-deity' };

export class PlayerModes {
  /** THE mode. Read everywhere; written only by `inhabit` and `ascend`. */
  mode: PlayerMode = 'deity';
  /** The mortal slot lit in the bar, 1–4, or null. Presentation state and nothing more. */
  mortalArmed: MortalVerb['slot'] | null = null;

  constructor(
    private readonly deity: DeityBody,
    readonly mortal: MortalPresence,
    private readonly locate: LocatePerson,
  ) {
    deity.active = true;
    mortal.active = false;
  }

  /**
   * Descend into a chosen life. Refuses — spending nothing, changing nothing —
   * unless a person is chosen and still in the world; otherwise seats the
   * mortal body on that person's present ground, facing the way the deity's
   * eye was facing, and hands it the input.
   *
   * The deity is left EXACTLY as it stood: its state is not touched, only its
   * input is dropped and its listeners put to sleep, so ascending finds the
   * eye where it was (brief §7).
   */
  inhabit(target: Target | null): InhabitResult {
    if (this.mode !== 'deity') return { ok: false, reason: 'not-deity' };
    if (!target || target.kind !== 'person') return { ok: false, reason: 'no-person' };
    const at = this.locate(target.id);
    if (!at) return { ok: false, reason: 'gone' };

    this.deity.clearInput();
    this.deity.active = false;
    this.mortal.seat(at.x, at.z, at.y, this.deity.yaw, target.id);
    this.mortal.active = true;
    this.mortalArmed = null;
    this.mode = 'mortal';
    return { ok: true, id: target.id, name: target.name };
  }

  /** Back to the deity, which is where it was. No event, no teleport. */
  ascend(): boolean {
    if (this.mode !== 'mortal') return false;
    this.mortal.active = false;
    this.mortal.release();
    this.deity.clearInput();
    this.deity.active = true;
    this.mortalArmed = null;
    this.mode = 'deity';
    return true;
  }

  /**
   * A mortal slot pressed: it lights, or goes out if it was lit, and that is
   * the whole of what happens. No simulation, no impulse, no world (brief §24).
   * Returns what lit, or null.
   */
  pressSlot(slot: MortalVerb['slot']): MortalVerb['slot'] | null {
    if (this.mode !== 'mortal') return null;
    this.mortalArmed = this.mortalArmed === slot ? null : slot;
    return this.mortalArmed;
  }
}
